import type { ParsedUrlQuery } from 'querystring';

import { useMemo } from 'react';
import type { ContestType } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { FEATURE } from '~/common/enums/feature.enum';
import { useTreatment } from '~/domains/common/hooks/useTreatment';
import { filterDescriptorFactory } from '~/domains/common/components/Filters/filterDescriptorFactory';
import type { TFilterObject } from '~/domains/common/components/Filters/filterDescriptorFactory';
import { useLeagues } from '~/domains/common/hooks/useLeagues';

import { ContestStatusFilter } from '../../services/enums/contest-status-filter.enum';

const useFieldDescriptors = () => {
  const { t } = useTranslation('contest');
  const { treatment } = useTreatment(FEATURE.CONFIG_LOBBY_FILTERS);
  const { data: leagues, isLoading: isLoadingLeagues } = useLeagues();

  const getGameTypeOptions = useMemo(() => {
    if (treatment?.treatment === 'on' && treatment?.config) {
      return (treatment.config as (ContestType & 'all')[]).map((type) => ({
        value: type,
        label: t(`entries.filters.gameType.options.${type}`),
      }));
    }

    return [];
  }, [t, treatment]);

  const getLeagueTypesOptions = useMemo(
    () =>
      leagues?.map((league) => ({
        value: league.alias,
        label: league.name,
      })) ?? [],
    [leagues]
  );

  const FilterDescriptors: Record<string, TFilterObject> = useMemo(
    () => ({
      SORT_BY_ACTIVE: {
        object: {
          title: t('entries.filters.sortBy.title'),
          key: 'SORT_BY',
          type: 'single-select',
          options: [
            { label: t('entries.filters.sortBy.options.nextSlateStarting') },
            {
              value: { option: 'ENTERED_DATE', dir: 'DESC' },
              label: t('entries.filters.sortBy.options.recentlyEntered'),
            },
            {
              value: { option: 'ENTRY_FEE', dir: 'DESC' },
              label: t('entries.filters.sortBy.options.highestEntryFee'),
            },
            {
              value: { option: 'ENTRY_FEE', dir: 'ASC' },
              label: t('entries.filters.sortBy.options.lowestEntryFee'),
            },
            {
              value: { option: 'PRIZE_POOL', dir: 'DESC' },
              label: t('entries.filters.sortBy.options.highestTotalPrizes'),
            },
          ],
        },
        fromQuery: (query: ParsedUrlQuery) => {
          const sortBy = query[URLQueryKeys.SORT_BY];
          if (!sortBy) {
            return null;
          }

          return [
            {
              option: query[URLQueryKeys.SORT_BY],
              dir: query[URLQueryKeys.SORT_ORDER] ?? 'ASC',
            },
          ];
        },
        toQuery: (value: { option: string; dir: string }[]) => {
          const v = value?.[0];

          if (!v) {
            return {
              [URLQueryKeys.SORT_BY]: null,
              [URLQueryKeys.SORT_ORDER]: null,
            };
          }

          return {
            [URLQueryKeys.SORT_BY]: v.option,
            [URLQueryKeys.SORT_ORDER]: v.dir,
          };
        },
      },
      SORT_BY_FINALIZED: {
        object: {
          title: t('entries.filters.sortBy.title'),
          key: 'SORT_BY',
          type: 'single-select',
          options: [
            {
              label: t('entries.filters.sortBy.options.recentlyEntered'),
            },
            {
              value: { option: 'ENTRY_FEE', dir: 'DESC' },
              label: t('entries.filters.sortBy.options.highestEntryFee'),
            },
            {
              value: { option: 'ENTRY_FEE', dir: 'ASC' },
              label: t('entries.filters.sortBy.options.lowestEntryFee'),
            },
            {
              value: { option: 'PRIZE_POOL', dir: 'DESC' },
              label: t('entries.filters.sortBy.options.highestTotalPrizes'),
            },
          ],
        },
        fromQuery: (query: ParsedUrlQuery) => {
          const sortBy = query[URLQueryKeys.SORT_BY];
          if (!sortBy) {
            return null;
          }

          return [
            {
              option: query[URLQueryKeys.SORT_BY],
              dir: query[URLQueryKeys.SORT_ORDER] ?? 'ASC',
            },
          ];
        },
        toQuery: (value: { option: string; dir: string }[]) => {
          const v = value?.[0];

          if (!v) {
            return {
              [URLQueryKeys.SORT_BY]: null,
              [URLQueryKeys.SORT_ORDER]: null,
            };
          }

          return {
            [URLQueryKeys.SORT_BY]: v.option,
            [URLQueryKeys.SORT_ORDER]: v.dir,
          };
        },
      },
      LEAGUES: {
        object: {
          title: t('entries.filters.league.title'),
          key: URLQueryKeys.LEAGUES,
          type: 'single-select',
          isLoading: isLoadingLeagues,
          options: [
            {
              value: undefined,
              label: t('entries.filters.league.options.all'),
            },
            ...getLeagueTypesOptions,
          ],
        },
      },
      GAME_TYPE: {
        object: {
          title: t('entries.filters.gameType.title'),
          key: URLQueryKeys.CONTEST_TYPE,
          type: 'single-select',
          options: [
            { value: undefined, label: t('entries.filters.gameType.options.all') },
            ...getGameTypeOptions,
          ],
        },
      },
      STATUS: {
        object: {
          title: t('entries.filters.status.title'),
          key: URLQueryKeys.CONTEST_STATUS,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('entries.filters.status.options.all'),
            },
            {
              value: ContestStatusFilter.ACTION_REQUIRED,
              label: t('entries.filters.status.options.missingPicks'),
            },
            {
              value: ContestStatusFilter.SCHEDULED,
              label: t('entries.filters.status.options.scheduled'),
            },
            {
              value: ContestStatusFilter.IN_PROGRESS,
              label: t('entries.filters.status.options.inProgress'),
            },
          ],
        },
        fromQuery: (query: ParsedUrlQuery) => {
          if (
            !query[URLQueryKeys.CONTEST_STATUS] ||
            query[URLQueryKeys.CONTEST_STATUS] === 'ACTIVE'
          ) {
            return null;
          }

          return [query[URLQueryKeys.CONTEST_STATUS]];
        },
      },
      HAS_ENTRY_FEE: {
        object: {
          title: t('entries.filters.hasEntryFee.title'),
          key: URLQueryKeys.HAS_ENTRY_FEE,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('entries.filters.hasEntryFee.options.all'),
            },
            {
              value: 'true',
              label: t('entries.filters.hasEntryFee.options.entryFee'),
            },
            {
              value: 'false',
              label: t('entries.filters.hasEntryFee.options.noEntryFee'),
            },
          ],
        },
      },
    }),
    [t, getGameTypeOptions, getLeagueTypesOptions, isLoadingLeagues]
  );

  return filterDescriptorFactory(FilterDescriptors);
};

export default useFieldDescriptors;
